import { Link } from '@tanstack/react-router';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { DataTableColumnDef } from '@nucleus-commerce/admin-kit/index-table';
import type { WebhookEndpoint } from '@nucleus-commerce/api-client/admin';
import { useFormat } from '@nucleus-commerce/i18n/react';
import { Badge } from '@nucleus-commerce/ui/components/badge';
import { cn } from '@nucleus-commerce/ui/lib/utils';

import { EndpointStatusBadge } from './webhook-badges';

const SHOWN_EVENTS = 3;

/**
 * Column model for the endpoints table: one row per endpoint with its subscriptions and
 * health. Sortable by URL, last success, last failure and failures in a row.
 */
export function useEndpointColumns(storeId: string) {
  const { t } = useTranslation('webhooks');
  const format = useFormat();

  return useMemo<Array<DataTableColumnDef<WebhookEndpoint>>>(() => {
    const when = (value: string | null) =>
      value ? (
        <span title={format.utc(value)}>{format.relative(value)}</span>
      ) : (
        <span className="text-muted-foreground">{t('never')}</span>
      );
    return [
      {
        id: 'url',
        accessorKey: 'url',
        header: t('columns.endpoint'),
        meta: { sortField: 'url', lockVisibility: true, cellClassName: 'max-w-72' },
        cell: ({ row }) => (
          <div className="flex min-w-0 flex-col gap-0.5">
            <Link
              to="/s/$storeId/settings/webhooks/$endpointId"
              params={{ storeId, endpointId: row.original.id }}
              className="text-foreground truncate font-mono text-[13px] font-medium hover:underline"
            >
              {row.original.url}
            </Link>
            {row.original.description ? (
              <span className="text-muted-foreground truncate text-xs">
                {row.original.description}
              </span>
            ) : null}
          </div>
        ),
      },
      {
        id: 'description',
        accessorFn: (row) => row.description ?? '',
        header: t('columns.description'),
        enableSorting: false,
      },
      {
        id: 'events',
        accessorKey: 'events',
        header: t('columns.events'),
        enableSorting: false,
        meta: { filterKey: 'event' },
        cell: ({ row }) => {
          const { events } = row.original;
          return (
            <div className="flex flex-wrap gap-1">
              {events.slice(0, SHOWN_EVENTS).map((event) => (
                <Badge key={event} variant="secondary" className="font-mono text-[11px]">
                  {event}
                </Badge>
              ))}
              {events.length > SHOWN_EVENTS ? (
                <Badge variant="outline" className="text-[11px]">
                  {t('moreEvents', { count: events.length - SHOWN_EVENTS })}
                </Badge>
              ) : null}
            </div>
          );
        },
      },
      {
        id: 'status',
        accessorKey: 'status',
        header: t('columns.status'),
        enableSorting: false,
        meta: { filterKey: 'status', searchable: false },
        cell: ({ row }) => <EndpointStatusBadge status={row.original.status} />,
      },
      {
        id: 'lastSuccessAt',
        accessorFn: (row) => row.lastSuccessAt ?? undefined,
        header: t('columns.lastSuccess'),
        sortUndefined: 'last',
        meta: { sortField: 'lastSuccessAt', searchable: false },
        cell: ({ row }) => when(row.original.lastSuccessAt),
      },
      {
        id: 'lastFailureAt',
        accessorFn: (row) => row.lastFailureAt ?? undefined,
        header: t('columns.lastFailure'),
        sortUndefined: 'last',
        meta: { sortField: 'lastFailureAt', searchable: false },
        cell: ({ row }) => when(row.original.lastFailureAt),
      },
      {
        id: 'failureCount',
        accessorKey: 'failureCount',
        header: t('columns.failures'),
        meta: { sortField: 'failureCount', align: 'end', searchable: false },
        cell: ({ row }) => (
          <span className={cn('tabular-nums', row.original.failureCount > 0 && 'text-destructive')}>
            {row.original.failureCount}
          </span>
        ),
      },
    ];
  }, [format, storeId, t]);
}
